import "./curbside.css";

import { Page } from "../page";
import { LocalizationService } from "../../../services/localization-service";
import EventEmitter from "eventemitter3";
import { Data } from "../../../models/data";
import { Context } from "../../../models/context";
import { EventTypes } from "../../../core/event-bus";
import { FieldType, Pages } from "../../../types/enums";
import { Util } from "../../../utils/util";
import { InputValidator } from '../../../validators/input-validator';
import { BrandService } from '../../../services/brand-service';
import { ICurbsideOptions } from "./curbside.interface";
import { LoaderSpinner } from "../../shared/LoaderSpinner/loader-spinner";
import { DataLayerService } from "../../../services/data-layer-service";
import { ValidationSubmitGuard } from "../../shared/Validation/validation-submit-guard";

export class Curbside extends Page {
    private readonly _brandService: BrandService;
    private readonly _dataLayerService: DataLayerService;
    private _userLatitude: number = 0;
    private _userLongitude: number = 0;
    private _options?: ICurbsideOptions;
    private _submitted: boolean = false;
    private _afterSubmitMessage: string = "Curbside_StoreHasBeenNotified";

    constructor(
        eventBus: EventEmitter,
        data: Data,
        context: Context,
        localizationService: LocalizationService,
        brandService: BrandService,
        dataLayerService: DataLayerService
    ) {
        super("page_curbside", eventBus, data, context, localizationService);
        this._brandService = brandService;
        this._dataLayerService = dataLayerService;
        this._eventBusListeners();
    }

    public async initAsync(): Promise<void> { }

    private _eventBusListeners() {
        this._eventBus.on(
            EventTypes.LOCATION_CHANGE,
            async (page: Pages, options?: ICurbsideOptions) => {
                if (page === Pages.CURBSIDE) {
                    this._options = options;
                    await this._renderPage();
                }
            }
        );

        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, async () => {
            await this._renderPage();
        });
    }

    public override show(): void {
        super.show();
        this._setEventListeners();
    }

    private _setEventListeners(): void {
        Util.setElement( "onclick", "btn_curbside_confirm", this._confirmButtonClick);
        Util.setElement( "onclick", "btn_curbside_back_to_home", this._backToHome);
        Util.setElement( "onclick", "btn_curbside_back_to_home_bottom", this._backToHome);
    }

    private async _renderPage(): Promise<void> {
        this._hideErrorElements();

        if (!this._submitted)
            await this._showFormSection();
        else
            this._showAfterSubmitSection();
    }

    private _hideErrorElements(): void {
        Util.hideElementErrors(
            "txt_curbside_car_info",
            "p_curbside_car_info_error"
        );
    }

    private async _showFormSection(): Promise<void> {
        this._setDefaultDistanceMessage();
        await this._getCoordinates();
        Util.hideElement("div_curbside_confirmation_section");
        Util.hideElement("div_curbside_back_to_home_section");
        Util.showElement("div_curbside_form");
    }

    private _setDefaultDistanceMessage(): void {
        const defaultDistanceMessage = this._localizationService.names("Curbside_LocationAccessDisabled");
        Util.setElement("innerHTML", "p_curbside_title", defaultDistanceMessage);
    }

    private async _getCoordinates(): Promise<void> {
        const pos = await Util.getCurrentPositionAsync();

        if (!pos) return;

        this._userLatitude = pos.latitude;
        this._userLongitude = pos.longitude;
        this._setDistanceMessage();
    }

    private _setDistanceMessage(): void {
        if (
            !this._userLatitude ||
            !this._userLongitude ||
            !this._options?.storeLatitude ||
            !this._options?.storeLongitude
        )
            return;

        let distance = Util.Float2(
            Util.GetDistance(
                this._userLatitude,
                this._userLongitude,
                this._options?.storeLatitude || 0,
                this._options?.storeLongitude || 0,
                "F"
            )
        );

        let unitName = this._localizationService.names("feet");

        if (distance > 1320) {
            unitName = this._localizationService.names("miles");
            distance = Util.Float2(distance * 0.00018939395);
        }

        const distanceMessage = this._localizationService
            .names("Curbside_EstimatedDistanceFromRestaurant")
            .replace("??", distance.toString())
            .replace("???", unitName);

        Util.setElement("innerHTML", "p_curbside_title", distanceMessage);
    }

    private _showAfterSubmitSection(): void {
        Util.hideElement("div_curbside_form");

        Util.setElement(
            "innerHTML",
            "p_curbside_confirmation_message",
            this._localizationService.names(this._afterSubmitMessage)
        );

        Util.showElement("div_curbside_confirmation_section");
        Util.showElement("div_curbside_back_to_home_section");
    }

    private readonly _confirmButtonClick = async (): Promise<void> => {
        if (!(await this._guardSubmitValidation())) {
            return;
        }

        const curbsideInfo = {
            lat: this._userLatitude,
            lng: this._userLongitude,
            car: Util.getElementValue("txt_curbside_car_info"),
            stall: "",
            sKy: this._options?.storeKey,
            oId: this._options?.orderId,
        }

        const spinner = new LoaderSpinner(this._element);
        const result = await this._brandService.curbsideHere(curbsideInfo);

        if (result) {
            if (result.success)
                this._afterSubmitMessage = "Curbside_StoreHasBeenNotified";
            else if (!result.success && result.message)
                this._afterSubmitMessage = result.message;

            this._submitted = true;
            await this._renderPage();
        }
        spinner.destroy();
    };

    private readonly _guardSubmitValidation = async (): Promise<boolean> => {
        return await ValidationSubmitGuard.validateAndAnnounce({
            container: this._element ?? this._id,
            validate: () => this._carInfoIsValid(),
            scrollToFirstInvalid: false
        });
    }

    private _carInfoIsValid(): boolean {
        return !InputValidator.validate(
            FieldType.REQUIRED,
            "txt_curbside_car_info",
            "p_curbside_car_info_error",
            true,
            true,
            undefined,
            undefined,
            this._localizationService,
            this._dataLayerService
        );
    }

    private readonly _backToHome = (): void => {
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LANDING);
    };
}
